import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { isSafari } from 'react-device-detect';

import { ExclamationCircleIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

import LoomSDKRecord from 'src/dashboard/content_forms/LoomSDKRecord';
import RTCCamera from 'src/record/RTCCamera';

import { StreamVideo } from 'src/record/StreamVideo';
import { useElementDimensionChange } from 'src/utils/element/dimension-change.hook';
import { fitWithin } from 'src/utils/fitWithin';
import { calculateTimeDuration } from 'src/record/helpers';

import { StepEditorContentFilePreview } from './file/preview';

//import { useStepEditorContext } from 'src/store/step-editor';
import { useFileUploader } from '../use-file-uploader.hook';
import { useStepEditorContext } from 'src/store/step-editor';
import { Nullable } from 'src/types/nullable.type';
import { EventBus } from 'src/common/EventBus';
import { functionNoop } from 'src/utils/function/noop';
import { StepEditorContentConfigureStageTypeEnum } from './configure/stage-type.enum';
import { getInlineFileSizeText } from 'src/common/helpers/get-inline-file-size-text';

export const StepEditorContentVideoRecorder = ({
  onPreviewReady = functionNoop,
  onLoomRecordingComplete = functionNoop,
  activeStage,
}: {
  activeStage: StepEditorContentConfigureStageTypeEnum;
  onPreviewReady?: (preview: ReactNode | null) => void;
  onLoomRecordingComplete?: (title: string, embedUrl: string) => void;
}) => {
  const [stream, setStream] = useState<Nullable<MediaStream>>(null);
  const [durationElapsed, setDurationElapsed] = useState<Nullable<number>>(null);
  const { uploadFile, uploadedFile, uploadInProgress, uploadPercentage, resetUploader } = useFileUploader();
  const [inlineMetaData, setInlineMetaData] = useState<Nullable<string>>(null);
  const {
    optimizeFullscreenMobile,
    setCurrentConfigurationStage,
    setName,
    setContentName,
    setOptimizeFullscreenMobile,
  } = useStepEditorContext();

  useEffect(() => {
    setOptimizeFullscreenMobile(true);
    return () => {
      resetUploader();
      setOptimizeFullscreenMobile(false);
      onPreviewReady(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stream) {
      onPreviewReady(
        <VideoPreview stream={stream} durationElapsed={durationElapsed} showMobileSafeZone={optimizeFullscreenMobile} />
      );
    } else {
      onPreviewReady(null);
    }
    // TODO: @anilsevim - adding onPreviewReady causes infinite invocation onPreviewReady, need to check back later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, optimizeFullscreenMobile, durationElapsed]);

  const onCancel = useCallback(() => {
    resetUploader();
  }, [resetUploader]);

  const onUploadCompleted = useCallback(
    (file: File) => {
      setInlineMetaData(getInlineFileSizeText(file).toLocaleUpperCase());
      setContentName(file.name);
      setName('Untitled Video');
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
    },
    [setContentName, setCurrentConfigurationStage]
  );

  const onNativeRecordingComplete = useCallback(
    async (file: File, duration?: number) => {
      await uploadFile(file);
      onUploadCompleted(file);
    },
    [uploadFile, onUploadCompleted]
  );

  const onLoomVideoInsertClicked = useCallback(
    (response: Record<string, string>) => {
      const { title, embedUrl } = response;
      setContentName(title);
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
      onLoomRecordingComplete(title, embedUrl);
    },
    [onLoomRecordingComplete, setCurrentConfigurationStage, setContentName]
  );

  if (isSafari) {
    return (
      <div className='text-gray-400 text-bedrock-p'>Video recording is only supported on Google Chrome currently.</div>
    );
  }

  if (
    [StepEditorContentConfigureStageTypeEnum.ANNOTATION, StepEditorContentConfigureStageTypeEnum.BEHAVIOR].includes(
      activeStage
    )
  ) {
    return null;
  }

  if (uploadedFile) {
    const onEditClicked = () => {
      EventBus.emit('editor:reset-content');
    };

    return (
      <StepEditorContentFilePreview
        file={uploadedFile}
        fileMeta={inlineMetaData}
        onUploadCompleted={onUploadCompleted}
        uploadInProgress={uploadInProgress}
        uploadPercentage={uploadPercentage}
        onEditClick={onEditClicked}
        disableEditing={uploadInProgress}
      />
    );
  }

  return (
    <>
      <h2 className='text-bedrock-p text-bedrock-black'>Record a video using Journey or Loom</h2>
      <RTCCamera
        videoEl={null}
        onPreviewStream={setStream}
        onComplete={onNativeRecordingComplete}
        onCancel={onCancel}
        onDurationElapsedUpdate={setDurationElapsed}
        onOptimizeFullscreenMobileChange={setOptimizeFullscreenMobile}
        optimizeFullscreenMobile={optimizeFullscreenMobile}
        hideOptimizeForMobileOption
        loomButton={<LoomSDKRecord autoTriggerRecord={false} onInsertClicked={onLoomVideoInsertClicked} />}
      />
    </>
  );
};

export const VideoPreview = ({
  stream,
  durationElapsed,
  showMobileSafeZone,
}: {
  stream: Nullable<MediaStream>;
  durationElapsed: Nullable<number>;
  showMobileSafeZone: boolean;
}) => {
  const videoDimensions = useElementDimensionChange();

  const videoWidth = videoDimensions.dimensions?.rect.width ?? 0;
  const videoHeight = videoDimensions.dimensions?.rect.height ?? 0;

  const mobilePreviewStyle = fitWithin({
    container: { width: videoWidth, height: videoHeight },
    aspectRatio: 16 / 10,
    padding: 0,
  });

  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <div className='max-h-full m-auto'>
      <div
        className={classNames(
          'relative rounded-lg overflow-hidden transition-opacity max-h-full max-w-full flex items-center justify-center',
          !videoLoaded && 'opacity-0'
        )}
      >
        <StreamVideo stream={stream} onReady={() => setVideoLoaded(true)} />
        {durationElapsed && (
          <div className='absolute left-4 bottom-4 bg-bedrock-black/backdrop backdrop-blur z-10 rounded-lg px-4 py-3'>
            <div className='flex items-center space-x-2'>
              <div className='rounded-full bg-bedrock-red w-4 h-4' />
              <div className='text-white text-bedrock-p-strong min-w-[40px]'>
                {calculateTimeDuration(durationElapsed || 0)}
              </div>
            </div>
          </div>
        )}
        <div
          ref={videoDimensions.ref}
          className={classNames(
            'absolute inset-0 bg-bedrock-black/transparent transition-opacity opacity-0',
            showMobileSafeZone && 'opacity-100'
          )}
        >
          <div
            className='rounded-2xl w-1/2 absolute left-0 inset-y-0 border border-white overflow-hidden -m-px'
            style={{ ...mobilePreviewStyle }}
          >
            <div className='absolute left-4 bottom-4 bg-bedrock-black/backdrop backdrop-blur z-10 rounded-lg px-4 py-3'>
              <div className='text-white text-bedrock-p-strong'>Mobile Preview</div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          'absolute top-0 left-0 right-0 rounded-lg overflow-hidden aspect-video transition-opacity',
          videoLoaded && 'opacity-0'
        )}
      >
        <div className='absolute inset-0 bg-gray-200 animate-pulse' />
      </div>
    </div>
  );
};
